import React, {useEffect, useState, useCallback, useRef} from 'react';
import {Collapse, Typography, Spin, message, Button, Checkbox} from 'antd';
import {RightOutlined, DownOutlined, LeftOutlined} from '@ant-design/icons';
import ProductSection from './ProductSection';
import {axiosInstance as axios} from '../../axios-config';
import {
  sanitizePhoneNumber,
  resolveMessagingPhoneNumber,
} from '../../utils/phone';

const {Panel} = Collapse;
const {Text} = Typography;
const PAGE_SIZE = 30;
// Meta refuses more than 30 products in one message, so a selection can never grow
// past this. We never disable the checkbox for it — an unresponsive tick reads as a
// bug — the agent is told the limit is full and what to do about it instead.
const MAX_SELECTION = 30;
const SELECTION_LIMIT_TOAST_KEY = 'selection-limit';
// Stored on a cart item added from the All Products list, which belongs to no
// collection but still needs its own badge.
const ALL_PRODUCTS_SECTION_ID = 'all-products';


// Base URL - Change this single variable to switch environments
const BASE_URL = 'https://restapis.bitespeed.co';

const COLLECTIONS_API = `${BASE_URL}/api/v1/chatbot/wa/getCollections`;
const PRODUCTS_BY_COLLECTION_API = `${BASE_URL}/api/v1/shop`; // /:storeId/products/searchByCollectionIds
const ALL_PRODUCTS_API = `${BASE_URL}/api/v1/chatwoot/getProducts`;
const CONTAINER_BASE_URL_SHOPPING = `${BASE_URL}/api/v1/shops/shopping`;

// List of shop URLs that should use product-wise view instead of collection-wise
const PRODUCT_VIEW_SHOP_URLS = [
  'bare-supplements.myshopify.com',
  'prashantistore.myshopify.com',
  'ugaoo-store.myshopify.com',
  'fix-my-curls.myshopify.com',

  // Example: 'example-store.myshopify.com'
];

// Mirror backend search: match product title OR any variant SKU (case-insensitive),
// so SKU searches (e.g. "KP-3") surface products whose title doesn't contain the term.
const productMatchesSearch = (product, term) => {
  const normalized = (term || '').trim().toLowerCase();
  if (!normalized) return true;
  const title = (product.title || '').toLowerCase();
  if (title.includes(normalized)) return true;
  return (product.variants || []).some(variant =>
    (variant.sku || '').toLowerCase().includes(normalized)
  );
};

// The cart pill on a section header. Deliberately quiet — this list can run to
// twenty near-identical rows inside Chatwoot's sidebar, so the indicator borrows
// the cart's own blue rather than competing for attention with a new colour. The
// only motion is a bump when the count CHANGES, which is the moment worth seeing.
const cartIndicatorStyles = `
  .ss-cart-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 20px;
    padding: 0 7px;
    margin-left: 8px;
    border-radius: 999px;
    background: #EDF6FF;
    border: 1px solid #CFE6FF;
    color: #0954A5;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    flex-shrink: 0;
    animation: ss-cart-pill-in 240ms cubic-bezier(0.2, 0.8, 0.3, 1) both;
  }
  .ss-cart-pill svg {
    display: block;
    flex-shrink: 0;
  }
  .ss-cart-pill--bumped {
    animation: ss-cart-pill-bump 460ms cubic-bezier(0.2, 0.8, 0.3, 1) both;
  }
  @keyframes ss-cart-pill-in {
    from { opacity: 0; transform: scale(0.82); }
    to { opacity: 1; transform: none; }
  }
  @keyframes ss-cart-pill-bump {
    0% { transform: none; box-shadow: 0 0 0 0 rgba(54, 158, 255, 0.4); }
    35% { transform: scale(1.09); }
    100% { transform: none; box-shadow: 0 0 0 8px rgba(54, 158, 255, 0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .ss-cart-pill,
    .ss-cart-pill--bumped {
      animation: none;
    }
  }

  /* This panel lives in a Chatwoot sidebar that can be dragged narrow, so every
     header row has to survive being squeezed. antd's own .ant-collapse-header-text
     has no min-width:0, which means a long collection title can never shrink and
     the whole list scrolls sideways instead — taking the checkbox off screen. */
  .ss-collections {
    overflow-x: hidden;
  }
  .ss-collections .ant-collapse-header {
    min-width: 0;
    padding-left: 10px !important;
    padding-right: 10px !important;
    align-items: center !important;
  }
  .ss-collections .ant-collapse-header-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
  }
  .ss-collections .ant-collapse-content-box {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  .ss-panel-header {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
  }
  .ss-panel-header__title {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
  }
  .ss-panel-header__send {
    flex-shrink: 0;
  }
  /* Two labels, one visible at a time: the long one reads better when there is
     room, the short one keeps the button tappable when there isn't. */
  .ss-send-label--compact {
    display: none;
  }

  @media (max-width: 420px) {
    .ss-send-label--full {
      display: none;
    }
    .ss-send-label--compact {
      display: inline;
    }
  }

  @media (max-width: 340px) {
    .ss-cart-pill {
      padding: 0 6px;
      margin-left: 6px;
    }
    .ss-cart-pill__label {
      display: none;
    }
    .ss-panel-header {
      gap: 6px;
    }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = cartIndicatorStyles;
  document.head.appendChild(styleSheet);
}

// Assigns each cart item that carries no provenance to exactly ONE section: the
// first, in display order, whose loaded products contain it.
//
// Provenance (`collection_id`, written when the item was added and persisted with
// the cart) is what makes the badge work on a cold load. Items added before it
// existed have none, and fall back to matching loaded products — but a product
// commonly belongs to several collections, so matching alone would count the same
// item on every one of them and the section badges would add up to more than the
// cart. One owner each keeps the numbers honest.
const getOwnerSectionByVariantId = (sections, cartItems) => {
  const owners = new Map();
  const unattributed = (cartItems || []).filter(item => !item.collection_id);
  if (!unattributed.length) {
    return owners;
  }
  sections.forEach(({sectionId, products}) => {
    const variantIds = new Set(
      (products || []).flatMap(product =>
        (product.variants || []).map(variant => variant.id)
      )
    );
    unattributed.forEach(item => {
      if (!owners.has(item.variant_id) && variantIds.has(item.variant_id)) {
        owners.set(item.variant_id, sectionId);
      }
    });
  });
  return owners;
};

// Total cart quantity a section contributed, so the section badges reconcile with
// the count on the cart button in the header.
const getCartQuantityForSection = (sectionId, cartItems, ownerByVariantId) =>
  (cartItems || []).reduce((total, item) => {
    const belongs = item.collection_id
      ? String(item.collection_id) === String(sectionId)
      : ownerByVariantId.get(item.variant_id) === sectionId;
    return belongs ? total + (item.quantity || 0) : total;
  }, 0);

// Glyph drawn inline rather than pulled from /cartIcon.svg: that asset is white
// (it sits on the blue cart button), while this one has to read as ink on a pale
// tint and stay crisp at 11px.
const CartGlyph = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M1.4 2.2h1.5l.9 1.9m0 0 2 4.4h6.1l2-4.4H3.8Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="6.4" cy="12.4" r="1.35" fill="currentColor" />
    <circle cx="11.5" cy="12.4" r="1.35" fill="currentColor" />
  </svg>
);

// Renders nothing at zero: an empty section should stay visually silent.
const CartIndicator = ({count}) => {
  const previousCountRef = useRef(count);
  // A counter rather than an on/off flag with a timer. Toggling a class off on a
  // timeout left the class stuck whenever the count dropped again inside that
  // window (the cleanup killed the timer and the "not grown" path never cleared
  // it), and after that no later increase could replay the animation, because
  // the class never changed. Bumping a key remounts the span, so the animation
  // restarts every time and there is no timer to leak.
  const [bumpSeq, setBumpSeq] = useState(0);

  useEffect(() => {
    const previousCount = previousCountRef.current;
    if (previousCount === count) {
      return;
    }
    previousCountRef.current = count;
    if (count > previousCount) {
      setBumpSeq(sequence => sequence + 1);
    }
  }, [count]);

  if (!count) {
    return null;
  }

  return (
    <span
      key={bumpSeq}
      className={`ss-cart-pill${bumpSeq > 0 ? ' ss-cart-pill--bumped' : ''}`}
      title={`${count} ${count === 1 ? 'item' : 'items'} from this section ${
        count === 1 ? 'is' : 'are'
      } in the cart`}
    >
      <CartGlyph />
      {count}
      <span className="ss-cart-pill__label">in cart</span>
    </span>
  );
};

const CollectionsScreen = ({
  shopUrl,
  shopPhoneNumber,
  cartItems,
  updateCartItems,
  updateCartItemsMulti,
  updateCartVariant,
  sendProductInChat,
  isSendInChatDisabled,
  searchValue = '',
  shopDetailsConfig,
  detailsFromChatwoot,
  currencySymbol,
  activePhoneNumber,
  // Selection and panel state are owned by App so that a trip to the cart screen,
  // which unmounts this component, does not wipe what the agent has picked.
  selectedByCollection,
  setSelectedByCollection,
  selectedAllProducts,
  setSelectedAllProducts,
  activePanelKey,
  setActivePanelKey,
  showAllProducts,
  setShowAllProducts,
}) => {
  // The hook API, not antd's static `message.*`: this package runs React 19,
  // where the static methods render through the removed ReactDOM.render and
  // silently do nothing. Every toast below used to be swallowed that way.
  const [messageApi, contextHolder] = message.useMessage();

  // One toast key for every limit warning, so repeated clicks replace the
  // message rather than stacking a column of identical ones.
  const warnSelectionLimitReached = () => {
    messageApi.warning({
      key: SELECTION_LIMIT_TOAST_KEY,
      content: `You can send up to ${MAX_SELECTION} products at a time. Unselect a product to select another.`,
    });
  };

  const warnSelectionLimitPartial = added => {
    messageApi.warning({
      key: SELECTION_LIMIT_TOAST_KEY,
      content: `Only ${added} more could be selected — ${MAX_SELECTION} products is the maximum that can be sent at a time.`,
    });
  };

  const [collections, setCollections] = useState([]);
  const [productsByCollection, setProductsByCollection] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState({});
  const [collectionPage, setCollectionPage] = useState({}); // { [collectionId]: pageNumber }

  // New state for all products
  const [allProducts, setAllProducts] = useState([]);
  const [loadingAllProducts, setLoadingAllProducts] = useState(false);

  // Whether the backend used Storefront search (set from API response)
  const [storefrontSearchUsed, setStorefrontSearchUsed] = useState(false);

  // Determine if this shop should force product-wise view
  const forceProductView = (PRODUCT_VIEW_SHOP_URLS || [])
    .map(s => (s || '').toLowerCase())
    .includes((shopUrl || '').toLowerCase());

  // Returns the products, so callers that need them right away (the header
  // checkbox on a collection that was never opened) don't have to wait a render.
  const fetchProductsForCollection = async collectionId => {
    if (productsByCollection[collectionId]) {
      return productsByCollection[collectionId];
    }
    setLoadingProducts(prev => ({...prev, [collectionId]: true}));
    try {
      const url = `${PRODUCTS_BY_COLLECTION_API}/${shopUrl}/products/searchByCollectionIds?collectionIds=${encodeURIComponent(String(collectionId))}`;
      const res = await axios.get(url);
      const apiProducts = res.data || [];

      // Map possible shapes to ProductSection format
      const mappedProducts = (apiProducts || [])
        .map(p => {
          const productId =
            p.productId || p.productid || p.id || p.shopifyId || p.shopifyid;
          const title = p.title || p.productName || p.productname || '';
          const image =
            p.image?.src || p.productImageUrl || p.productimageurl || null;
          const variantsArray = Array.isArray(p.variants) ? p.variants : [];
          const variants = variantsArray
            .map(v => ({
              id: v.id || v.variantId || v.variantid,
              title: v.title || v.variantName || v.variantname,
              price: v.price || v.variantPrice || v.variantprice,
              sku: v.sku || v.variantSku || v.variantsku,
              image:
                v.image?.src ||
                v.imageUrl ||
                v.variantImageUrl ||
                v.variantimageurl ||
                image,
            }))
            .filter(v => v.id);
          return {title, productId, variants, image};
        })
        .filter(
          mp =>
            mp.productId && Array.isArray(mp.variants) && mp.variants.length > 0
        );

      setProductsByCollection(prev => ({
        ...prev,
        [collectionId]: mappedProducts,
      }));
      return mappedProducts;
    } catch (e) {
      messageApi.error('Error fetching products for collection');
      console.error('Error fetching products for collection', e);
      return [];
    } finally {
      setLoadingProducts(prev => ({...prev, [collectionId]: false}));
    }
  };

  // Optimized search function for collections with indexed parameters
  const fetchProductsForCollectionWithSearch = useCallback(
    async (collectionId, searchTerm) => {
      setLoadingProducts(prev => ({...prev, [collectionId]: true}));
      try {
        const url = `${PRODUCTS_BY_COLLECTION_API}/${shopUrl}/products/searchByCollectionIds?collectionIds=${encodeURIComponent(String(collectionId))}`;
        const res = await axios.get(url);
        const apiProducts = res.data || [];
        const mappedProducts = (apiProducts || [])
          .map(p => {
            const productId =
              p.productId || p.productid || p.id || p.shopifyId || p.shopifyid;
            const title = p.title || p.productName || p.productname || '';
            const image =
              p.image?.src || p.productImageUrl || p.productimageurl || null;
            const variantsArray = Array.isArray(p.variants) ? p.variants : [];
            const variants = variantsArray
              .map(v => ({
                id: v.id || v.variantId || v.variantid,
                title: v.title || v.variantName || v.variantname,
                price: v.price || v.variantPrice || v.variantprice,
                sku: v.sku || v.variantSku || v.variantsku,
                image:
                  v.image?.src ||
                  v.imageUrl ||
                  v.variantImageUrl ||
                  v.variantimageurl ||
                  image,
              }))
              .filter(v => v.id);
            return {title, productId, variants, image};
          })
          .filter(
            mp =>
              mp.productId &&
              Array.isArray(mp.variants) &&
              mp.variants.length > 0
          )
          .filter(mp =>
            (mp.title || '')
              .toLowerCase()
              .includes((searchTerm || '').toLowerCase())
          );

        setProductsByCollection(prev => ({
          ...prev,
          [collectionId]: mappedProducts,
        }));
      } catch (e) {
        messageApi.error('Error fetching products for collection');
        console.error('Error fetching products for collection (search)', e);
      } finally {
        setLoadingProducts(prev => ({...prev, [collectionId]: false}));
      }
    },
    [shopUrl, messageApi]
  );

  // Fetch all products
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAllProducts = useCallback(async () => {
    if (loadingAllProducts) return;
    setLoadingAllProducts(true);
    try {
      // Fetch all products
      const allProductsResponse = await axios.get(
        `${ALL_PRODUCTS_API}/${shopUrl}?pageSize=1000&pageNumber=0`
      );
      console.log('All Products API Response:', allProductsResponse.data);
      const products = allProductsResponse.data.products || [];
      console.log('Products from API:', products);

      // Map to the format expected by ProductSection
      const mappedProducts = products.map(product => ({
        title: product.title,
        productId: product.id,
        variants: (product.variants || []).map(variant => ({
          id: variant.id,
          title: variant.title,
          price: variant.price,
          sku: variant.sku,
          image: variant.image?.src || product.image?.src,
        })),
        image: product.image?.src,
      }));

      console.log('Mapped Products:', mappedProducts);
      setAllProducts(mappedProducts);
    } catch (e) {
      messageApi.error('Error fetching all products');
      console.error('Error fetching all products:', e);
    } finally {
      setLoadingAllProducts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopUrl]);

  // Optimized search function for all products with indexed parameters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAllProductsWithSearch = useCallback(
    async searchTerm => {
      if (loadingAllProducts) return;
      setLoadingAllProducts(true);
      try {
        // Fetch products with search optimization (align query param with backend)
        const allProductsResponse = await axios.get(
          `${ALL_PRODUCTS_API}/${shopUrl}?search=${encodeURIComponent(searchTerm)}&useIndex=true&limit=100&pageSize=50&pageNumber=0`
        );
        const products = allProductsResponse.data.products || [];
        setStorefrontSearchUsed(
          allProductsResponse.data.storefrontSearchUsed === true
        );

        // Map to the format expected by ProductSection
        const mappedProducts = products.map(product => ({
          title: product.title,
          productId: product.id,
          variants: (product.variants || []).map(variant => ({
            id: variant.id,
            title: variant.title,
            price: variant.price,
            sku: variant.sku,
            image: variant.image?.src || product.image?.src,
          })),
          image: product.image?.src,
        }));

        setAllProducts(mappedProducts);
      } catch (e) {
        messageApi.error('Error fetching all products');
        console.error('Error fetching all products:', e);
      } finally {
        setLoadingAllProducts(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shopUrl]
  );

  useEffect(() => {
    if (!shopUrl) return;
    // If product-wise view is forced, skip collections and fetch all products instead
    if (forceProductView) {
      setCollections([]);
      setLoading(false);
      setShowAllProducts(true);
      fetchAllProducts();
      return;
    }
    setLoading(true);
    axios
      .get(COLLECTIONS_API, {params: {shopUrl}})
      .then(res => {
        setCollections(res.data?.collections || res.data || []);
        setLoading(false);
      })
      .catch(() => {
        messageApi.error('Error fetching collections');
        setLoading(false);
      });
  }, [
    shopUrl,
    forceProductView,
    fetchAllProducts,
    setShowAllProducts,
    messageApi,
  ]);

  // App keeps the open panels across a trip to the cart, but the products they
  // hold are fetched here and die with the unmount — so fetch them again once.
  const didRestorePanelsRef = useRef(false);
  useEffect(() => {
    if (didRestorePanelsRef.current || !shopUrl || forceProductView) {
      return;
    }
    didRestorePanelsRef.current = true;
    if (activePanelKey) {
      fetchProductsForCollection(activePanelKey);
    }
    if (showAllProducts) {
      fetchAllProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopUrl, forceProductView]);

  // Debounced search effect: search both collection names and product titles
  // Tracks the term the last run acted on, so clearing loaded products stays tied
  // to a search actually being cleared instead of firing on every mount.
  const lastSearchTermRef = useRef((searchValue || '').trim().toLowerCase());

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const term = (searchValue || '').trim().toLowerCase();
      const previousTerm = lastSearchTermRef.current;
      lastSearchTermRef.current = term;

      // For product-wise view shops, only search across all products
      if (forceProductView) {
        if (term) {
          fetchAllProductsWithSearch(term);
        } else if (previousTerm) {
          setAllProducts([]);
        }
        return;
      }

      if (term) {
        // Filter collections by name
        const matchingCollections = collections.filter(c => {
          const name = (c.title || c.name || '').toLowerCase();
          return name.includes(term);
        });

        // Load products for matching collections (by collection API), then filter titles client-side
        matchingCollections.forEach(collection => {
          const collectionId = collection.id || collection.shopifyId;
          fetchProductsForCollectionWithSearch(collectionId, term);
        });

        // Also fetch all products with search for the All Products section
        fetchAllProductsWithSearch(term);
      } else if (previousTerm) {
        setAllProducts([]);
        setProductsByCollection({});
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    searchValue,
    collections,
    fetchAllProductsWithSearch,
    fetchProductsForCollectionWithSearch,
    forceProductView,
  ]);

  // Auto-expand collections and all products when searching
  const lastExpandSearchRef = useRef(searchValue);
  useEffect(() => {
    const previousSearch = lastExpandSearchRef.current;
    lastExpandSearchRef.current = searchValue;
    if (forceProductView) {
      setShowAllProducts(true);
      return;
    }
    if (searchValue && searchValue.trim()) {
      // Auto-expand All Products section immediately
      setShowAllProducts(true);

      // Auto-expand first collection that might have matching products
      if (collections.length > 0) {
        const firstCollectionId = collections[0].id || collections[0].shopifyId;
        setActivePanelKey(firstCollectionId);
      }
    } else if (previousSearch && previousSearch.trim()) {
      // Reset when a search is cleared — but not on mount, where this would close
      // the panels restored from App state.
      setActivePanelKey(null);
      setShowAllProducts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, collections, forceProductView]);

  // Calculate total selected checkboxes across all collections
  const totalSelectedCount = Object.values(selectedByCollection).reduce(
    (acc, arr) => acc + arr.length,
    0
  );

  // Handle checkbox selection with max limit, only for current page
  // `variantIds` is every variant currently chosen on one product card, so a single
  // tick can add or remove several variants of the same product at once.
  const handleProductCheckbox = (collectionId, variantIds, checked) => {
    const ids = Array.isArray(variantIds) ? variantIds : [variantIds];
    if (!ids.length) {
      return;
    }
    if (!checked) {
      setSelectedByCollection(prev => ({
        ...prev,
        [collectionId]: (prev[collectionId] || []).filter(
          id => !ids.includes(id)
        ),
      }));
      return;
    }
    const prevSelected = selectedByCollection[collectionId] || [];
    const notSelected = ids.filter(id => !prevSelected.includes(id));
    if (!notSelected.length) {
      return;
    }
    const toAdd = notSelected.slice(
      0,
      Math.max(0, MAX_SELECTION - totalSelectedCount)
    );
    if (!toAdd.length) {
      warnSelectionLimitReached();
      return;
    }
    if (toAdd.length < notSelected.length) {
      warnSelectionLimitPartial(toAdd.length);
    }
    setSelectedByCollection(prev => ({
      ...prev,
      [collectionId]: [...(prev[collectionId] || []), ...toAdd],
    }));
  };

  // Select/deselect all products in a collection for the current page only
  const handleCollectionCheckbox = (collectionId, checked, pageProductIds) => {
    if (checked) {
      // Calculate how many can be added
      const alreadySelected = totalSelectedCount;
      const notSelected = pageProductIds.filter(
        id => !(selectedByCollection[collectionId] || []).includes(id)
      );
      const canAdd = Math.max(0, MAX_SELECTION - alreadySelected);
      const toAdd = notSelected.slice(0, canAdd);
      const newSelected = [
        ...(selectedByCollection[collectionId] || []).filter(
          id => !pageProductIds.includes(id)
        ),
        ...toAdd,
      ];
      if (notSelected.length && !toAdd.length) {
        warnSelectionLimitReached();
        return;
      }
      if (toAdd.length < notSelected.length) {
        warnSelectionLimitPartial(toAdd.length);
      }
      setSelectedByCollection(prev => ({
        ...prev,
        [collectionId]: newSelected,
      }));
    } else {
      // Deselect only the current page's products
      setSelectedByCollection(prev => ({
        ...prev,
        [collectionId]: (prev[collectionId] || []).filter(
          id => !pageProductIds.includes(id)
        ),
      }));
    }
  };

  // Handle checkbox selection for all products
  const handleAllProductsCheckbox = (variantIds, checked) => {
    const ids = Array.isArray(variantIds) ? variantIds : [variantIds];
    if (!ids.length) {
      return;
    }
    if (!checked) {
      setSelectedAllProducts(prev => prev.filter(id => !ids.includes(id)));
      return;
    }
    const notSelected = ids.filter(id => !selectedAllProducts.includes(id));
    if (!notSelected.length) {
      return;
    }
    const toAdd = notSelected.slice(
      0,
      Math.max(0, MAX_SELECTION - selectedAllProducts.length)
    );
    if (!toAdd.length) {
      warnSelectionLimitReached();
      return;
    }
    if (toAdd.length < notSelected.length) {
      warnSelectionLimitPartial(toAdd.length);
    }
    setSelectedAllProducts(prev => [...prev, ...toAdd]);
  };

  // `activePhoneNumber` comes from the shipping details, which an agent can
  // type a country-code-less number into. Prefer the Chatwoot contact number
  // instead of making them go and fix it.
  const getMessagingPhoneNumber = () => {
    const {phoneNumber, error, corrected} = resolveMessagingPhoneNumber(
      activePhoneNumber,
      sanitizePhoneNumber(
        shopDetailsConfig?.phoneNumber || detailsFromChatwoot?.phoneNumber
      )
    );
    if (error) {
      messageApi.error(error);
      return null;
    }
    if (corrected) {
      console.warn(
        `Shipping phone ${sanitizePhoneNumber(
          activePhoneNumber
        )} is not this conversation's contact; messaging ${phoneNumber} instead.`
      );
    }
    return phoneNumber;
  };

  // Send all selected products in chat for a collection
  const handleSendSelected = async collectionId => {
    const selected = selectedByCollection[collectionId] || [];
    if (!selected.length) {
      messageApi.warning('No products selected');
      return;
    }

    const isInstagram = detailsFromChatwoot.instagramSourceId;
    const isWhatsApp = activePhoneNumber;

    if (!isInstagram && !isWhatsApp) {
      messageApi.error('Please provide contact information to send products');
      return;
    }

    try {
      if (isInstagram) {
        // Instagram flow - batch send in groups of 10 (carousel limit)
        if (!detailsFromChatwoot.instagramPageId) {
          messageApi.error('Instagram page not configured');
          return;
        }

        const BATCH_SIZE = 10;
        const batches = [];
        for (let i = 0; i < selected.length; i += BATCH_SIZE) {
          batches.push(selected.slice(i, i + BATCH_SIZE));
        }

        for (const batch of batches) {
          await axios.post(`${CONTAINER_BASE_URL_SHOPPING}/sendInstagramCollection`, {
            shopUrl: shopUrl,
            instagramPageId: detailsFromChatwoot.instagramPageId,
            instagramSourceId: detailsFromChatwoot.instagramSourceId || detailsFromChatwoot.igsid,
            variantIds: batch,
            chatWootConversationId: detailsFromChatwoot.conversationId,
          });
        }

        messageApi.success(`${selected.length} products sent on Instagram!`);
        setSelectedByCollection(prev => ({...prev, [collectionId]: []}));
      } else if (isWhatsApp) {
        // WhatsApp flow (existing logic)
        const messagingPhoneNumber = getMessagingPhoneNumber();
        if (!messagingPhoneNumber) {
          return;
        }

        axios
          .post(`${CONTAINER_BASE_URL_SHOPPING}/sendCollection`, {
            shopUrl: shopUrl,
            shopPhoneNumber: shopPhoneNumber,
            phoneNumber: messagingPhoneNumber,
            collectionId: collectionId,
            variantIds: selected,
            chatWootConversationId: detailsFromChatwoot.conversationId,
          })
          .then(() => {
            messageApi.success('Collection sent successfully!');
            setSelectedByCollection(prev => ({...prev, [collectionId]: []}));
          })
          .catch(async () => {
            // Fallback: send products individually to avoid losing functionality
            try {
              await Promise.all(
                selected.map(variantId =>
                  axios.post(`${CONTAINER_BASE_URL_SHOPPING}/sendProductItem`, {
                    shopUrl: shopUrl,
                    phoneNumber: messagingPhoneNumber,
                    variantId,
                  })
                )
              );
              messageApi.success('Sent selected products individually.');
              setSelectedByCollection(prev => ({...prev, [collectionId]: []}));
            } catch (e) {
              messageApi.error('Error sending selected products');
            }
          });
      }
    } catch (error) {
      messageApi.error('Error sending products');
      console.error(error);
    }
  };

  // Send selected all products
  const handleSendSelectedAllProducts = async () => {
    if (!selectedAllProducts.length) {
      messageApi.warning('No products selected');
      return;
    }

    const isInstagram = detailsFromChatwoot.instagramSourceId;
    const isWhatsApp = activePhoneNumber;

    if (!isInstagram && !isWhatsApp) {
      messageApi.error('Please provide contact information to send products');
      return;
    }

    try {
      if (isInstagram) {
        // Instagram flow - batch send in groups of 10 (carousel limit)
        if (!detailsFromChatwoot.instagramPageId) {
          messageApi.error('Instagram page not configured');
          return;
        }

        const BATCH_SIZE = 10;
        const batches = [];
        for (let i = 0; i < selectedAllProducts.length; i += BATCH_SIZE) {
          batches.push(selectedAllProducts.slice(i, i + BATCH_SIZE));
        }

        for (const batch of batches) {
          await axios.post(`${CONTAINER_BASE_URL_SHOPPING}/sendInstagramCollection`, {
            shopUrl: shopUrl,
            instagramPageId: detailsFromChatwoot.instagramPageId,
            instagramSourceId: detailsFromChatwoot.instagramSourceId || detailsFromChatwoot.igsid,
            variantIds: batch,
            chatWootConversationId: detailsFromChatwoot.conversationId,
          });
        }

        messageApi.success(`${selectedAllProducts.length} products sent on Instagram!`);
        setSelectedAllProducts([]);
      } else if (isWhatsApp) {
        // WhatsApp flow - send each product individually
        const messagingPhoneNumber = getMessagingPhoneNumber();
        if (!messagingPhoneNumber) {
          return;
        }

        Promise.all(
          selectedAllProducts.map(variantId =>
            axios.post(`${CONTAINER_BASE_URL_SHOPPING}/sendProductItem`, {
              shopUrl: shopUrl,
              phoneNumber: messagingPhoneNumber,
              variantId: variantId,
            })
          )
        )
          .then(() => {
            messageApi.success('Products sent successfully!');
            setSelectedAllProducts([]);
          })
          .catch(() => {
            messageApi.error('Error sending products');
          });
      }
    } catch (error) {
      messageApi.error('Error sending products');
      console.error(error);
    }
  };

  // Custom expand icon that only toggles on icon click and fetches products
  const renderExpandIcon = ({isActive, panelKey}) => (
    <span
      onClick={async e => {
        e.stopPropagation();
        console.log(
          'Expand icon clicked for panel:',
          panelKey,
          'isActive:',
          isActive
        );

        if (panelKey === 'all-products') {
          console.log(
            'All Products panel clicked, current showAllProducts:',
            showAllProducts
          );
          if (!showAllProducts) {
            console.log(
              'Setting showAllProducts to true and calling fetchAllProducts'
            );
            setShowAllProducts(true);
            await fetchAllProducts();
          } else {
            console.log('Setting showAllProducts to false');
            setShowAllProducts(false);
          }
        } else {
          // Handle collection panels
          console.log(
            'Collection panel clicked:',
            panelKey,
            'isActive:',
            isActive
          );
          if (isActive) {
            console.log('Closing collection panel:', panelKey);
            setActivePanelKey(null);
          } else {
            console.log('Opening collection panel:', panelKey);
            setActivePanelKey(panelKey);
            await fetchProductsForCollection(panelKey);
          }
        }
      }}
      style={{cursor: 'pointer', marginLeft: 8}}
    >
      {(panelKey === 'all-products' ? showAllProducts : isActive) ? (
        <DownOutlined />
      ) : (
        <RightOutlined />
      )}
    </span>
  );

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        {contextHolder}
        <Spin size="large" />
      </div>
    );
  }

  // Filter collections to show only those that have matching products when searching
  const filteredCollections =
    searchValue && searchValue.trim()
      ? collections.filter(collection => {
          const collectionId = collection.id || collection.shopifyId;
          const products = productsByCollection[collectionId] || [];
          if (storefrontSearchUsed) {
            // Backend already filtered — just check if any products loaded
            return products.length > 0;
          }
          // Client-side title/SKU filtering for shops without Storefront search
          return products.some(product =>
            productMatchesSearch(product, searchValue)
          );
        })
      : collections;

  // Filter all products by search value (client-side) unless Storefront search handles it
  const filteredAllProducts =
    !storefrontSearchUsed && searchValue && searchValue.trim()
      ? allProducts.filter(product =>
          productMatchesSearch(product, searchValue)
        )
      : allProducts;

  // Display order decides ownership, so the badge lands on the section the agent
  // sees first rather than on whichever collection happened to load first.
  const ownerByVariantId = getOwnerSectionByVariantId(
    [
      ...filteredCollections.map(collection => {
        const collectionId = collection.id || collection.shopifyId;
        return {
          sectionId: collectionId,
          products: productsByCollection[collectionId] || [],
        };
      }),
      {sectionId: ALL_PRODUCTS_SECTION_ID, products: filteredAllProducts},
    ],
    cartItems
  );

  // In forced product-only view, render direct list without any dropdown/panel
  if (forceProductView) {
    return (
      <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        {contextHolder}
        {loadingAllProducts ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <ProductSection
            products={filteredAllProducts}
            updateCartItems={updateCartItems}
            updateCartItemsMulti={updateCartItemsMulti}
            cartItems={cartItems}
            updateCartVariant={updateCartVariant}
            sendProductInChat={sendProductInChat}
            isSendInChatDisabled={isSendInChatDisabled}
            currencySymbol={currencySymbol}
            collectionId={ALL_PRODUCTS_SECTION_ID}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      {contextHolder}
      {/* Collections and All Products Section */}
      {collections.length === 0 && filteredAllProducts.length === 0 ? (
        <Text>No collections or products found.</Text>
      ) : (
        <Collapse
          className="ss-collections"
          style={{background: '#fff', flex: 1, overflowY: 'auto'}}
          expandIconPosition="end"
          activeKey={[
            ...(showAllProducts ? ['all-products'] : []),
            ...(activePanelKey ? [activePanelKey] : []),
          ]}
          onChange={keys => {
            console.log('Collapse onChange triggered with keys:', keys);

            // Handle All Products panel expansion
            if (keys.includes('all-products')) {
              console.log('All products key detected in onChange');
              if (!showAllProducts) {
                console.log('Opening All Products panel and fetching products');
                setShowAllProducts(true);
                fetchAllProducts();
              }
            } else {
              if (showAllProducts && !forceProductView) {
                console.log('Closing All Products panel');
                setShowAllProducts(false);
              }
            }

            // Handle collection panel expansion
            const collectionKey = keys.find(key => key !== 'all-products');
            if (
              !forceProductView &&
              collectionKey &&
              collectionKey !== activePanelKey
            ) {
              setActivePanelKey(collectionKey);
              fetchProductsForCollection(collectionKey);
            } else if (!forceProductView && !collectionKey && activePanelKey) {
              setActivePanelKey(null);
            }
          }}
          expandIcon={panelProps =>
            renderExpandIcon({
              isActive:
                panelProps.panelKey === 'all-products'
                  ? showAllProducts
                  : activePanelKey === panelProps.panelKey,
              panelKey: panelProps.panelKey,
            })
          }
        >
          {/* Collections */}
          {!forceProductView &&
            filteredCollections.map(collection => {
              const collectionId = collection.id || collection.shopifyId;
              const products = productsByCollection[collectionId] || [];

              // For Storefront-enabled shops, backend already filtered; otherwise filter client-side
              const filteredProducts =
                storefrontSearchUsed || !searchValue
                  ? products
                  : products.filter(product =>
                      productMatchesSearch(product, searchValue)
                    );

              // Pagination logic
              const page = collectionPage[collectionId] || 1;
              const totalPages = Math.ceil(filteredProducts.length / PAGE_SIZE);
              const startIdx = (page - 1) * PAGE_SIZE;
              const endIdx = startIdx + PAGE_SIZE;
              const pageProducts = filteredProducts.slice(startIdx, endIdx);
              // Get all variant IDs in this page
              const pageProductIds = pageProducts.flatMap(product =>
                (product.variants || []).map(variant => variant.id)
              );
              const checkedVariantIds = (
                selectedByCollection[collectionId] || []
              ).filter(id => pageProductIds.includes(id));
              const allChecked =
                pageProductIds.length > 0 &&
                checkedVariantIds.length === pageProductIds.length;
              const someChecked =
                checkedVariantIds.length > 0 &&
                checkedVariantIds.length < pageProductIds.length;
              // Counted across the whole collection, not just the visible page:
              // an item added on page 2 still belongs to this section.
              const cartQuantityInCollection = getCartQuantityForSection(
                collectionId,
                cartItems,
                ownerByVariantId
              );
              // Panel header with checkbox and send button
              const panelHeader = (
                <div className="ss-panel-header">
                  <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    // A plain toggle rather than `e.target.checked`: a partial
                    // selection renders indeterminate, so antd reports every
                    // click on it as "check", and once the 30-product limit
                    // stopped the selection growing there was no way left to
                    // clear it. Anything selected -> the click clears it.
                    onChange={async () => {
                      if (checkedVariantIds.length) {
                        handleCollectionCheckbox(
                          collectionId,
                          false,
                          pageProductIds
                        );
                        return;
                      }
                      // Ticking a collection that was never expanded has no
                      // variant ids to work with yet — fetch them first so the
                      // click isn't silently dropped.
                      let idsToSelect = pageProductIds;
                      if (!idsToSelect.length) {
                        const fetched = await fetchProductsForCollection(
                          collectionId
                        );
                        idsToSelect = (fetched || [])
                          .slice(0, PAGE_SIZE)
                          .flatMap(product =>
                            (product.variants || []).map(variant => variant.id)
                          );
                      }
                      handleCollectionCheckbox(collectionId, true, idsToSelect);
                    }}
                    style={{marginRight: 8}}
                  />
                  <Text
                    className="ss-panel-header__title"
                    title={collection.title || collection.name}
                  >
                    {collection.title || collection.name}
                  </Text>
                  <CartIndicator count={cartQuantityInCollection} />
                  {/* Only once something is selected — a disabled button on every
                      one of twenty rows ate the width the titles needed, and the
                      All Products header already works this way. */}
                  {checkedVariantIds.length > 0 && (
                    <Button
                      className="ss-panel-header__send"
                      type="primary"
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleSendSelected(collectionId);
                      }}
                    >
                      <span className="ss-send-label--full">
                        Send Selected in Chat
                      </span>
                      <span className="ss-send-label--compact">
                        Send ({checkedVariantIds.length})
                      </span>
                    </Button>
                  )}
                </div>
              );
              return (
                <Panel header={panelHeader} key={collectionId}>
                  {loadingProducts[collectionId] ? (
                    <Spin />
                  ) : (
                    <>
                      <ProductSection
                        products={pageProducts}
                        updateCartItems={updateCartItems}
                        updateCartItemsMulti={updateCartItemsMulti}
                        cartItems={cartItems}
                        updateCartVariant={updateCartVariant}
                        collectionId={collectionId}
                        showCheckboxes
                        onCheckboxChange={(variantIds, checked) =>
                          handleProductCheckbox(
                            collectionId,
                            variantIds,
                            checked
                          )
                        }
                        checkedVariantIds={checkedVariantIds}
                        sendProductInChat={sendProductInChat}
                        isSendInChatDisabled={isSendInChatDisabled}
                        currencySymbol={currencySymbol}
                      />
                      {totalPages > 1 && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: 16,
                          }}
                        >
                          <Button
                            icon={<LeftOutlined />}
                            disabled={page === 1}
                            onClick={e => {
                              e.stopPropagation();
                              setCollectionPage(prev => ({
                                ...prev,
                                [collectionId]: page - 1,
                              }));
                            }}
                            style={{marginRight: 8}}
                          />
                          <span>
                            Page {page} of {totalPages}
                          </span>
                          <Button
                            icon={<RightOutlined />}
                            disabled={page === totalPages}
                            onClick={e => {
                              e.stopPropagation();
                              setCollectionPage(prev => ({
                                ...prev,
                                [collectionId]: page + 1,
                              }));
                            }}
                            style={{marginLeft: 8}}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Panel>
              );
            })}

          {/* All Products Section */}
          <Panel
            header={
              <div className="ss-panel-header">
                <Checkbox
                  checked={selectedAllProducts.length > 0}
                  indeterminate={
                    selectedAllProducts.length > 0 &&
                    selectedAllProducts.length < allProducts.length
                  }
                  onChange={e => {
                    if (e.target.checked) {
                      const allVariantIds = allProducts.flatMap(product =>
                        (product.variants || []).map(variant => variant.id)
                      );
                      const toSelect = allVariantIds.slice(0, MAX_SELECTION);
                      if (toSelect.length < allVariantIds.length) {
                        warnSelectionLimitPartial(toSelect.length);
                      }
                      setSelectedAllProducts(toSelect);
                    } else {
                      setSelectedAllProducts([]);
                    }
                  }}
                  style={{marginRight: 8}}
                />
                {!forceProductView && (
                  <>
                    <Text className="ss-panel-header__title">All Products</Text>
                    <CartIndicator
                      count={getCartQuantityForSection(
                        ALL_PRODUCTS_SECTION_ID,
                        cartItems,
                        ownerByVariantId
                      )}
                    />
                  </>
                )}
                {selectedAllProducts.length > 0 && (
                  <Button
                    className="ss-panel-header__send"
                    type="primary"
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      handleSendSelectedAllProducts();
                    }}
                  >
                    Send ({selectedAllProducts.length})
                  </Button>
                )}
              </div>
            }
            key="all-products"
          >
            {showAllProducts && (
              <>
                {console.log(
                  'Rendering All Products section - loadingAllProducts:',
                  loadingAllProducts,
                  'filteredAllProducts.length:',
                  filteredAllProducts.length
                )}
                {loadingAllProducts ? (
                  <Spin />
                ) : filteredAllProducts.length > 0 ? (
                  <ProductSection
                    products={filteredAllProducts}
                    updateCartItems={updateCartItems}
                    updateCartItemsMulti={updateCartItemsMulti}
                    cartItems={cartItems}
                    updateCartVariant={updateCartVariant}
                    collectionId={ALL_PRODUCTS_SECTION_ID}
                    showCheckboxes
                    onCheckboxChange={handleAllProductsCheckbox}
                    checkedVariantIds={selectedAllProducts}
                    sendProductInChat={sendProductInChat}
                    isSendInChatDisabled={isSendInChatDisabled}
                    currencySymbol={currencySymbol}
                  />
                ) : searchValue ? (
                  <Text>No products found matching "{searchValue}".</Text>
                ) : (
                  <Text>No products found.</Text>
                )}
              </>
            )}
          </Panel>
        </Collapse>
      )}
    </div>
  );
};

export default CollectionsScreen;
